import { useEffect, useRef } from 'react'
import { useAnimations } from '@react-three/drei'
import useGame from '../stores/useGame.jsx'

/**
 * Weapons System Animation Controller
 * Controls the F-35 weapons bay animations
 * 
 * BombBay Animation Rules:
 * - Opens ONLY when SEC001 is clicked
 * - Closes when:
 *   1. SEC001 Back button is clicked
 *   2. Any other SEC marker (SEC002, SEC003, SEC004) is clicked
 * - Does NOT close when SEC005 or SEC006 is clicked (sub-sections of SEC001)
 */
export function WeaponsSystemAnim({ animations, modelRef }) {
  const { actions, mixer } = useAnimations(animations, modelRef)
  const bombBaysRef = useRef(null)
  const soloActionRef = useRef(null)
  const pjdActionRef = useRef(null)
  const fireActionRef = useRef(null)
  const animDirectionRef = useRef(null)        // 'opening' | 'closing' | null
  const reverseRafRef = useRef(null)           // rAF handle for the closing poll
  const openFinishedListenerRef = useRef(null) // mixer 'finished' listener from playBombBaysForward
  
  // Get states from store
  const weaponsBayOpen = useGame((state) => state.weaponsBayOpen)
  const weaponsBayAnimating = useGame((state) => state.weaponsBayAnimating)
  const setWeaponsBayOpen = useGame((state) => state.setWeaponsBayOpen)
  const setWeaponsBayAnimating = useGame((state) => state.setWeaponsBayAnimating)
  const setPjdamFireActive = useGame((state) => state.setPjdamFireActive)
  
  // Initialize animations
  useEffect(() => {
    if (!actions || Object.keys(actions).length === 0) {
      return
    }
    
    const bombName = 'BombBays'
    const soloName = 'PJDAM_Solo_Action'
    const pjdName = 'PJDAM_FireDrop_Action'
    const fireName = 'PJDAM_Action_Fire'
    
    const bombAction = actions[bombName]
    const soloAction = actions[soloName]
    const pjdAction = actions[pjdName]
    const fireAction = actions[fireName]
    
    if (bombAction) {
      bombBaysRef.current = bombAction
      bombAction.clampWhenFinished = true
      bombAction.setLoop(2200, 1) // LoopOnce
      bombAction.timeScale = 1
    }
    
    if (soloAction) {
      soloActionRef.current = soloAction
      soloAction.clampWhenFinished = true
      soloAction.setLoop(2200, 1)
      soloAction.timeScale = 1
    }
    
    if (pjdAction) {
      pjdActionRef.current = pjdAction
      pjdAction.clampWhenFinished = true
      pjdAction.setLoop(2200, 1)
      pjdAction.timeScale = 1
    }
    
    if (fireAction) {
      fireActionRef.current = fireAction
      fireAction.clampWhenFinished = true
      fireAction.setLoop(2200, 1)
      fireAction.timeScale = 1
    }
    
    return () => {
      if (bombAction) bombAction.stop()
      if (soloAction) soloAction.stop()
      if (pjdAction) pjdAction.stop()
      if (fireAction) fireAction.stop()
    }
  }, [actions])
  
  // Keep latest values in refs so the stable event listeners can read them
  // without being torn down and re-attached on every state change.
  const weaponsBayOpenRef = useRef(weaponsBayOpen)
  const weaponsBayAnimatingRef = useRef(weaponsBayAnimating)
  useEffect(() => { weaponsBayOpenRef.current = weaponsBayOpen }, [weaponsBayOpen])
  useEffect(() => { weaponsBayAnimatingRef.current = weaponsBayAnimating }, [weaponsBayAnimating])

  // Handle SEC001 specific logic — registered once, reads state via refs
  useEffect(() => {
    const handleSecSelected = (event) => {
      const secName = event.detail?.secName
      
      // OPEN: Only SEC001 opens the bomb bay
      if (secName === 'SEC001') {
        if (!weaponsBayOpenRef.current && !weaponsBayAnimatingRef.current) {
          playBombBaysForward()
        }
      }
      // CLOSE: Any SEC except SEC001, SEC005, SEC006 closes the bomb bay
      else if (secName !== 'SEC005' && secName !== 'SEC006') {
        if (weaponsBayOpenRef.current || animDirectionRef.current === 'opening') {
          playBombBaysReverse()
        }
      }
    }
    
    const handleSecDeselected = (event) => {
      const secName = event.detail?.secName
      const toSecName = event.detail?.toSecName

      // CLOSE: SEC001 deselected — but not if switching into a sub-section
      if (secName === 'SEC001') {
        if (toSecName === 'SEC005' || toSecName === 'SEC006') return
        // Close if fully open OR if still opening (interrupt mid-animation)
        if (weaponsBayOpenRef.current || animDirectionRef.current === 'opening') {
          playBombBaysReverse()
        }
      }

      // CLOSE: SEC006 deselected back to default
      if (secName === 'SEC006' && !toSecName) {
        if (weaponsBayOpenRef.current || animDirectionRef.current === 'opening') {
          playBombBaysReverse()
        }
      }
    }
    
    window.addEventListener('sec-selected', handleSecSelected)
    window.addEventListener('sec-deselected', handleSecDeselected)
    
    return () => {
      window.removeEventListener('sec-selected', handleSecSelected)
      window.removeEventListener('sec-deselected', handleSecDeselected)
    }
  }, []) // stable — state is read via refs above
  
  // Animation Functions
  
  const playBombBaysForward = () => {
    const action = bombBaysRef.current
    if (!action) return

    // Cancel any in-progress closing poll
    if (reverseRafRef.current) {
      cancelAnimationFrame(reverseRafRef.current)
      reverseRafRef.current = null
    }

    setWeaponsBayAnimating(true)
    animDirectionRef.current = 'opening'

    action.reset()
    action.timeScale = 1
    action.paused = false
    action.play()

    const onFinished = (e) => {
      if (e.action === action) {
        setWeaponsBayOpen(true)
        setWeaponsBayAnimating(false)
        animDirectionRef.current = null
        action.paused = true
        mixer.removeEventListener('finished', onFinished)
        openFinishedListenerRef.current = null
      }
    }

    // Store so playBombBaysReverse can remove it if it interrupts
    openFinishedListenerRef.current = onFinished
    mixer.addEventListener('finished', onFinished)
  }
  
  const playBombBaysReverse = () => {
    const action = bombBaysRef.current
    if (!action) return

    // If already closing, don't start a second reverse
    if (animDirectionRef.current === 'closing') return

    // Cancel any in-progress closing poll (shouldn't happen, but be safe)
    if (reverseRafRef.current) {
      cancelAnimationFrame(reverseRafRef.current)
      reverseRafRef.current = null
    }

    // If we're interrupting a forward animation, remove its finished listener
    // so it doesn't fire when the action reaches its boundary during reverse
    if (openFinishedListenerRef.current) {
      mixer.removeEventListener('finished', openFinishedListenerRef.current)
      openFinishedListenerRef.current = null
    }

    setWeaponsBayAnimating(true)
    animDirectionRef.current = 'closing'

    // If the bay was fully open (paused at end), seek to end first.
    // If interrupting a mid-play forward animation, use its current time.
    if (!action.isRunning()) {
      action.time = action.getClip().duration
    }
    action.paused = false
    action.timeScale = -1
    action.play()

    const checkComplete = () => {
      if (action.time <= 0.01) {
        action.time = 0
        action.paused = true
        action.stop()
        setWeaponsBayOpen(false)
        setWeaponsBayAnimating(false)
        animDirectionRef.current = null
        reverseRafRef.current = null
      } else {
        reverseRafRef.current = requestAnimationFrame(checkComplete)
      }
    }

    reverseRafRef.current = requestAnimationFrame(checkComplete)
  }
  
  // Cinematic Mode Animation Sequence
  // 1. BombBays opens first; waits for mixer 'finished' before proceeding.
  // 2. Once fully open, PJDAM_Solo_Action + PJDAM_FireDrop_Action + PJDAM_Action_Fire
  //    all start simultaneously and Jet VFX activates.
  // 3. When PJDAM_FireDrop_Action finishes, BombBays reverses closed.
  // 4. rAF poll detects close complete → reset all clips + hide VFX.
  const playCinematicSequence = () => {
    const bombAction = bombBaysRef.current
    const soloAction = soloActionRef.current
    const pjdAction  = pjdActionRef.current
    const fireAction = fireActionRef.current

    if (!bombAction || !pjdAction) return

    setWeaponsBayAnimating(true)

    bombAction.reset()
    bombAction.timeScale = 1
    bombAction.paused = false
    bombAction.clampWhenFinished = true
    bombAction.play()

    const onBombOpen = (e) => {
      if (e.action !== bombAction) return
      mixer.removeEventListener('finished', onBombOpen)
      bombAction.paused = true

      ;[
        { action: soloAction },
        { action: pjdAction  },
        { action: fireAction },
      ].forEach(({ action }) => {
        if (!action) return
        action.reset()
        action.timeScale = 1
        action.paused = false
        action.clampWhenFinished = true
        action.play()
      })

      setTimeout(() => {
        setPjdamFireActive(true)
      }, 500)

      const onPjdFinished = (e) => {
        if (e.action !== pjdAction) return
        mixer.removeEventListener('finished', onPjdFinished)

        window.dispatchEvent(new CustomEvent('cinematic-weapon-complete'))

        const bomb = bombBaysRef.current
        if (!bomb) return

        setWeaponsBayOpen(false)

        bomb.paused = false
        bomb.time = bomb.getClip().duration
        bomb.timeScale = -1
        bomb.play()

        let animationFrame
        const checkBombReversed = () => {
          if (bomb.time <= 0.01) {
            bomb.time = 0
            bomb.paused = true
            bomb.stop()

            ;[soloActionRef, pjdActionRef, fireActionRef].forEach(ref => {
              if (!ref.current) return
              ref.current.stop()
              ref.current.time = 0
              ref.current.paused = true
            })

            setPjdamFireActive(false)
            setWeaponsBayAnimating(false)
            cancelAnimationFrame(animationFrame)
          } else {
            animationFrame = requestAnimationFrame(checkBombReversed)
          }
        }
        animationFrame = requestAnimationFrame(checkBombReversed)
      }

      mixer.addEventListener('finished', onPjdFinished)
    }

    mixer.addEventListener('finished', onBombOpen)
  }
  
  // Listen for cinematic camera ready (after camera has reached cinematic position)
  useEffect(() => {
    if (!mixer || !actions) return
    
    const handleCinematicCameraReady = () => {
      playCinematicSequence()
    }
    
    window.addEventListener('cinematic-camera-ready', handleCinematicCameraReady)
    return () => {
      window.removeEventListener('cinematic-camera-ready', handleCinematicCameraReady)
    }
  }, [mixer, actions])
  
  return null
}
